import { create } from 'zustand';
import apiClient from '../lib/api';

/**
 * Asset Hub store. Every value comes from the assets service
 * (/api/assets/* → assets.* tables). There is no browser cache and no
 * invented defaults: an empty column stays empty and the UI shows "—".
 * Every action throws an Error carrying the server's message, so screens can
 * report failures instead of pretending the change was saved.
 */

// ───────────────────────────── types ─────────────────────────────

export type AssetStatus = 'In Stock' | 'Assigned' | 'Under Maintenance' | 'Retired' | 'Lost' | 'Disposed';
export const ASSET_STATUSES: AssetStatus[] = ['In Stock', 'Assigned', 'Under Maintenance', 'Retired', 'Lost', 'Disposed'];

export interface AssetFile {
  id: number;
  kind: 'image' | 'invoice' | 'warranty' | 'document';
  fileName: string;
  fileUrl: string;
  mimeType?: string | null;
  sizeBytes?: number | null;
  metadata: Record<string, any>;
  uploadedAt: string;
}

/** Invoice view of an 'invoice' attachment (metadata holds number/amount/vendor/date). */
export interface AssetInvoice {
  id: string;
  invoiceNumber: string;
  vendorName: string;
  amount: number;
  currency?: string;
  invoiceDate?: string;
  fileUrl: string;
  fileName: string;
  fileSize?: string;
  uploadedAt: string;
}

/** Document view of a 'warranty' / 'document' attachment. */
export interface AssetAttachment {
  id: string;
  name: string;
  type: string;
  fileUrl: string;
  fileName: string;
  fileSize?: string;
  uploadedAt: string;
}

export interface Asset {
  id: string;
  asset_code: string;
  /** Alias of asset_code kept for existing screens. */
  code: string;
  name: string;
  type: string;
  category?: string | null;
  subcategory?: string | null;
  status: AssetStatus | string;

  manufacturer?: string | null;
  model?: string | null;
  serial_number?: string | null;
  assetTag?: string | null;

  /** Employee id (people.employees) the asset is assigned to. */
  assignedTo?: string | null;
  assignedToName?: string | null;
  assignedToEmail?: string | null;
  assignedOn?: string | null;
  /** Live details of the assignee from people.employees. */
  assignee?: {
    code: string | null; designation: string | null; department: string | null; email: string | null;
    phone: string | null; location: string | null; status: string | null; managerName: string | null;
  } | null;

  purchaseDate?: string | null;
  purchaseCost?: number | null;
  cost?: number | null;
  supplierId?: number | null;
  vendor?: string | null;
  poNumber?: string | null;
  invoiceNumber?: string | null;
  warrantyStart?: string | null;
  warrantyUntil?: string | null;

  department?: string | null;
  location?: string | null;
  site?: string | null;
  officeId?: string | null;
  costCentre?: string | null;
  assetOwner?: string | null;

  os?: string | null;
  osVersion?: string | null;
  ram?: string | null;
  storage?: string | null;
  cpuSpeed?: string | null;
  macAddress?: string | null;
  ipAddress?: string | null;

  condition?: string | null;
  lifecycleStage?: string | null;
  disposalDate?: string | null;
  disposalMethod?: string | null;
  notes?: string | null;
  remarks?: string | null;
  specifications: Record<string, any>;

  files: AssetFile[];
  images: string[];
  invoices: AssetInvoice[];
  attachments: AssetAttachment[];

  createdAt?: string;
  updatedAt?: string;
}

export interface AssetRequest {
  id: string;
  request_number?: string;
  requestNumber?: string;
  requester_id?: string;
  requester_name: string;
  requester_email?: string;
  requester_employee_id?: string | null;
  requester_department?: string | null;
  department?: string | null;
  asset_type: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  urgency?: string;
  reason?: string;
  status: 'pending' | 'approved' | 'rejected' | 'fulfilled';
  delivery_location?: string | null;
  estimated_cost?: number | null;
  notes?: string | null;
  ticket_id?: string | null;
  ticket_no?: string | null;
  ticketId?: string | null;
  ticketNo?: string | null;
  support_ticket_id?: string | number | null;
  supportTicketId?: string | number | null;
  specifications?: Record<string, any>;
  reviewer_name?: string | null;
  approval_notes?: string | null;
  assigned_asset_id?: string | null;
  assigned_asset_code?: string | null;
  assigned_asset_name?: string | null;
  reviewed_at?: string | null;
  fulfilled_at?: string | null;
  createdAt?: string;
}

export type MaintenanceStatus = 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
export interface MaintenanceJob {
  id: string;
  assetId: string;
  assetCode: string;
  assetName: string;
  assetStatus: string;
  maintenanceType: string;
  description?: string | null;
  scheduledDate?: string | null;
  completedDate?: string | null;
  cost?: number | null;
  vendor?: string | null;
  supplierId?: number | null;
  supplierName?: string | null;
  notes?: string | null;
  status: MaintenanceStatus;
  ticketId?: string | number | null;
  ticketNo?: string | null;
  ticket_id?: string | number | null;
  ticket_no?: string | null;
  createdAt: string;
}

export type PurchaseOrderStatus = 'draft' | 'ordered' | 'received' | 'cancelled';
export interface PurchaseOrder {
  id: string;
  poNumber: string;
  supplierId?: number | null;
  vendorName?: string | null;
  itemDescription: string;
  category?: string | null;
  quantity: number;
  unitCost: number;
  totalCost: number;
  currency: string;
  status: PurchaseOrderStatus;
  expectedDate?: string | null;
  receivedAt?: string | null;
  receivedAssets: number;
  notes?: string | null;
  orderDate: string;
  ticketId?: string | number | null;
  ticketNo?: string | null;
  ticket_id?: string | number | null;
  ticket_no?: string | null;
}

export interface AuditLog {
  id: string;
  assetId?: string | null;
  assetCode: string;
  assetName: string;
  action: string;
  details?: string | null;
  fromStatus?: string | null;
  toStatus?: string | null;
  performedBy: string;
  timestamp: string;
}

// ───────────────────────────── mapping ─────────────────────────────

const num = (v: any): number | null => (v === null || v === undefined || v === '' ? null : Number(v));
/** DATE columns arrive as 'YYYY-MM-DD'; timestamptz ones as ISO — keep the calendar day. */
const day = (v: any): string | null => (v ? String(v).slice(0, 10) : null);
const errorOf = (e: any, fallback: string) => new Error(e?.response?.data?.message || e?.message || fallback);

function formatSize(bytes?: number | null) {
  if (!bytes) return undefined;
  return bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function mapFile(f: any): AssetFile {
  return {
    id: f.id,
    kind: f.kind,
    fileName: f.file_name || f.filename || f.fileName || 'file',
    fileUrl: f.file_url || f.path || f.fileUrl || '',
    mimeType: f.mime_type || f.mimeType,
    sizeBytes: f.size_bytes === null || f.size_bytes === undefined ? (f.sizeBytes ?? null) : Number(f.size_bytes),
    metadata: f.metadata || {},
    uploadedAt: f.uploaded_at || f.created_at || f.uploadedAt || new Date().toISOString(),
  };
}

export function mapAsset(row: any): Asset {
  const files: AssetFile[] = (row.attachments || []).map(mapFile);
  const specs = row.specifications && typeof row.specifications === 'object' ? row.specifications : {};
  return {
    id: row.id,
    asset_code: row.asset_code,
    code: row.asset_code,
    name: row.name,
    type: row.type,
    category: row.category,
    subcategory: row.subcategory,
    status: row.status,
    manufacturer: row.manufacturer,
    model: row.model,
    serial_number: row.serial_number,
    assetTag: row.asset_tag,
    assignedTo: row.assigned_to_id,
    assignedToName: row.assigned_to_name,
    assignedToEmail: row.assigned_user_email,
    assignedOn: day(row.deployment_date),
    assignee: row.assigned_to_id && (row.assignee_employee_code !== undefined || row.assignee_email !== undefined)
      ? {
          code: row.assignee_employee_code ?? null, designation: row.assignee_designation ?? null,
          department: row.assignee_department ?? null, email: row.assignee_email ?? null,
          phone: row.assignee_phone ?? null, location: row.assignee_location ?? null,
          status: row.assignee_status ?? null, managerName: row.assignee_manager_name ?? null,
        }
      : null,
    purchaseDate: day(row.purchase_date),
    purchaseCost: num(row.purchase_cost),
    cost: num(row.purchase_cost),
    supplierId: row.supplier_id,
    vendor: row.supplier_name,
    poNumber: row.po_number,
    invoiceNumber: row.invoice_number,
    warrantyStart: day(row.warranty_start),
    warrantyUntil: day(row.warranty_end_date),
    department: row.department,
    location: row.location,
    site: row.site,
    costCentre: row.cost_centre,
    assetOwner: row.asset_owner,
    os: row.os,
    osVersion: row.os_version,
    ram: row.ram,
    storage: row.storage,
    cpuSpeed: row.cpu,
    macAddress: row.mac_address,
    ipAddress: row.ip_address,
    condition: row.condition,
    lifecycleStage: row.lifecycle_stage,
    disposalDate: day(row.disposal_date),
    disposalMethod: row.disposal_method,
    notes: row.notes,
    remarks: row.remarks,
    specifications: specs,
    files,
    images: files.filter((f) => f.kind === 'image').map((f) => f.fileUrl),
    invoices: files.filter((f) => f.kind === 'invoice').map((f) => ({
      id: String(f.id),
      invoiceNumber: f.metadata.invoice_number || '',
      vendorName: f.metadata.vendor || '',
      amount: Number(f.metadata.amount) || 0,
      currency: f.metadata.currency,
      invoiceDate: f.metadata.invoice_date,
      fileUrl: f.fileUrl,
      fileName: f.fileName,
      fileSize: formatSize(f.sizeBytes),
      uploadedAt: f.uploadedAt,
    })),
    attachments: files.filter((f) => f.kind === 'warranty' || f.kind === 'document').map((f) => ({
      id: String(f.id),
      name: f.metadata.title || f.fileName,
      type: f.kind,
      fileUrl: f.fileUrl,
      fileName: f.fileName,
      fileSize: formatSize(f.sizeBytes),
      uploadedAt: f.uploadedAt,
    })),
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

/** camelCase form fields → assets.assets columns. Unknown keys are dropped. */
const FIELD_MAP: Record<string, string> = {
  asset_code: 'asset_code', code: 'asset_code', name: 'name', type: 'type', category: 'category', subcategory: 'subcategory',
  manufacturer: 'manufacturer', model: 'model', serial_number: 'serial_number', serialNumber: 'serial_number', assetTag: 'asset_tag',
  purchaseDate: 'purchase_date', purchaseCost: 'purchase_cost', cost: 'purchase_cost', supplierId: 'supplier_id',
  poNumber: 'po_number', invoiceNumber: 'invoice_number', warrantyStart: 'warranty_start', warrantyUntil: 'warranty_end_date',
  department: 'department', location: 'location', site: 'site', costCentre: 'cost_centre', assetOwner: 'asset_owner',
  os: 'os', osVersion: 'os_version', ram: 'ram', storage: 'storage', cpuSpeed: 'cpu', macAddress: 'mac_address', ipAddress: 'ip_address',
  condition: 'condition', lifecycleStage: 'lifecycle_stage', notes: 'notes', remarks: 'remarks', status: 'status',
  specifications: 'specifications',
};

export function toAssetPayload(data: Record<string, any>) {
  const out: Record<string, any> = {};
  for (const [key, value] of Object.entries(data)) {
    const col = FIELD_MAP[key];
    if (!col || value === undefined) continue;
    out[col] = typeof value === 'string' ? value.trim() : value;
  }
  return out;
}

const mapRequest = (r: any): AssetRequest => ({
  ...r,
  ticket_id: r.ticket_id || r.ticket_no || r.ticketId || r.ticketNo || null,
  ticket_no: r.ticket_no || r.ticket_id || r.ticketNo || r.ticketId || null,
  ticketId: r.ticketId || r.ticket_id || r.ticket_no || r.ticketNo || null,
  ticketNo: r.ticketNo || r.ticket_no || r.ticket_id || r.ticketId || null,
  support_ticket_id: r.support_ticket_id || r.supportTicketId || null,
  supportTicketId: r.supportTicketId || r.support_ticket_id || null,
  specifications: r.specifications && typeof r.specifications === 'object' ? r.specifications : {},
});

const mapJob = (m: any): MaintenanceJob => ({
  id: m.id,
  assetId: m.asset_id,
  assetCode: m.asset_code,
  assetName: m.asset_name,
  assetStatus: m.asset_status,
  maintenanceType: m.maintenance_type,
  description: m.description,
  scheduledDate: day(m.scheduled_date),
  completedDate: day(m.completed_date),
  cost: num(m.cost),
  vendor: m.vendor,
  supplierId: m.supplier_id,
  supplierName: m.supplier_name,
  notes: m.notes,
  status: m.status,
  ticketId: m.ticket_no || m.ticket_id || m.ticketNo || m.ticketId || null,
  ticketNo: m.ticket_no || m.ticket_id || m.ticketNo || m.ticketId || null,
  ticket_id: m.ticket_id || m.ticket_no || null,
  ticket_no: m.ticket_no || m.ticket_id || null,
  createdAt: m.created_at,
});

const mapPo = (p: any): PurchaseOrder => ({
  id: p.id,
  poNumber: p.po_number,
  supplierId: p.supplier_id,
  vendorName: p.supplier_name,
  itemDescription: p.item_description,
  category: p.category,
  quantity: Number(p.quantity),
  unitCost: Number(p.unit_cost) || 0,
  totalCost: Number(p.total_cost) || 0,
  currency: p.currency || 'INR',
  status: p.status,
  expectedDate: day(p.expected_date),
  receivedAt: p.received_at,
  receivedAssets: Number(p.received_assets) || 0,
  notes: p.notes,
  orderDate: p.created_at,
  ticketId: p.ticket_id || p.ticketId,
  ticketNo: p.ticket_no || p.ticketNo,
  ticket_id: p.ticket_id || p.ticketId,
  ticket_no: p.ticket_no || p.ticketNo,
});

const mapLog = (h: any): AuditLog => ({
  id: h.id,
  assetId: h.asset_id,
  assetCode: h.asset_code || '',
  assetName: h.asset_name || '',
  action: h.action,
  details: h.details,
  fromStatus: h.from_status,
  toStatus: h.to_status,
  performedBy: h.performed_by_name || '—',
  timestamp: h.created_at,
});

const dataOf = (res: any) => res.data?.data ?? res.data;

// ───────────────────────────── store ─────────────────────────────

interface AssetState {
  assets: Asset[];
  requests: AssetRequest[];
  maintenance: MaintenanceJob[];
  purchaseOrders: PurchaseOrder[];
  auditLogs: AuditLog[];
  currentAsset: Asset | null;
  isLoading: boolean;
  /** True once fetchAssets has completed at least once (distinguishes "loading" from "empty"). */
  assetsLoaded: boolean;
  error: string | null;

  fetchAssets: (filters?: Record<string, any>) => Promise<void>;
  fetchAsset: (idOrCode: string) => Promise<Asset>;
  nextAssetCode: (category?: string) => Promise<string>;
  createAsset: (data: Record<string, any>) => Promise<Asset>;
  updateAsset: (id: string, data: Record<string, any>) => Promise<Asset>;
  deleteAsset: (id: string) => Promise<void>;
  assignAsset: (id: string, employeeId: string, notes?: string, meta?: { department?: string; site?: string; location?: string; employeeName?: string }) => Promise<Asset>;
  returnAsset: (id: string, data?: { condition?: string; notes?: string }) => Promise<Asset>;
  retireAsset: (id: string, data?: { reason?: string; disposalMethod?: string }) => Promise<Asset>;
  addAttachment: (id: string, file: { kind: AssetFile['kind']; filename: string; path: string; mimeType?: string; sizeBytes?: number; metadata?: Record<string, any> }) => Promise<Asset>;
  updateAttachment: (id: string, attachmentId: number | string, data: { fileName?: string; kind?: string; metadata?: Record<string, any> }) => Promise<Asset>;
  deleteAttachment: (id: string, attachmentId: number | string) => Promise<Asset>;

  fetchRequests: () => Promise<void>;
  createRequest: (data: { asset_type: string; priority?: string; reason?: string; [key: string]: any }) => Promise<AssetRequest>;
  updateRequestStatus: (id: string, status: 'approved' | 'rejected', meta?: Record<string, any>) => Promise<void>;
  fulfillRequest: (id: string, assetId: string) => Promise<void>;

  fetchMaintenance: (filters?: { assetId?: string; status?: MaintenanceStatus }) => Promise<void>;
  createMaintenance: (data: { assetId: string; maintenance_type: string; scheduled_date: string; description?: string; vendor?: string; supplier_id?: number | null; cost?: number | null; notes?: string }) => Promise<MaintenanceJob>;
  updateMaintenance: (id: string, data: Record<string, any>) => Promise<void>;
  deleteMaintenance: (id: string) => Promise<void>;

  fetchPurchaseOrders: () => Promise<void>;
  nextPoNumber: () => Promise<string>;
  createPurchaseOrder: (data: Record<string, any>) => Promise<PurchaseOrder>;
  updatePurchaseOrder: (id: string, data: Record<string, any>) => Promise<void>;
  receivePurchaseOrder: (id: string) => Promise<number>;
  deletePurchaseOrder: (id: string) => Promise<void>;

  fetchHistory: (filters?: { assetId?: string; limit?: number }) => Promise<AuditLog[]>;

  clearCurrentAsset: () => void;
  clearError: () => void;
}

export const useAssetStore = create<AssetState>((set, get) => {
  /** Replace one asset in the list after a write. */
  const upsert = (asset: Asset) =>
    set((s) => ({
      assets: s.assets.some((a) => a.id === asset.id) ? s.assets.map((a) => (a.id === asset.id ? asset : a)) : [asset, ...s.assets],
      currentAsset: s.currentAsset?.id === asset.id ? asset : s.currentAsset,
    }));

  const assetAction = async (fn: () => Promise<any>, fallback: string) => {
    try {
      const asset = mapAsset(dataOf(await fn()));
      upsert(asset);
      return asset;
    } catch (e) {
      throw errorOf(e, fallback);
    }
  };

  return {
    assets: [],
    requests: [],
    maintenance: [],
    purchaseOrders: [],
    auditLogs: [],
    currentAsset: null,
    isLoading: false,
    assetsLoaded: false,
    error: null,

    fetchAssets: async (filters) => {
      set({ isLoading: true, error: null });
      try {
        const res = await apiClient.get('/assets/assets', { params: { pageSize: 1000, ...filters } });
        const rows = dataOf(res);
        set({ assets: Array.isArray(rows) ? rows.map(mapAsset) : [], isLoading: false, assetsLoaded: true });
      } catch (e: any) {
        set({ isLoading: false, assetsLoaded: true, error: errorOf(e, 'Could not load assets').message });
      }
    },

    fetchAsset: async (idOrCode) => {
      try {
        const asset = mapAsset(dataOf(await apiClient.get(`/assets/assets/${encodeURIComponent(idOrCode)}`)));
        set({ currentAsset: asset });
        upsert(asset);
        return asset;
      } catch (e) {
        set({ currentAsset: null });
        throw errorOf(e, 'Could not load this asset');
      }
    },

    nextAssetCode: async (category) => {
      try {
        return dataOf(await apiClient.get('/assets/assets/next-id', { params: { category } })).asset_code;
      } catch (e) {
        throw errorOf(e, 'Could not generate an asset code');
      }
    },

    createAsset: (data) => assetAction(() => apiClient.post('/assets/assets', toAssetPayload(data)), 'Could not register the asset'),
    updateAsset: (id, data) => assetAction(() => apiClient.patch(`/assets/assets/${id}`, toAssetPayload(data)), 'Could not save the asset'),

    deleteAsset: async (id) => {
      try {
        await apiClient.delete(`/assets/assets/${id}`);
        set((s) => ({ assets: s.assets.filter((a) => a.id !== id), currentAsset: s.currentAsset?.id === id ? null : s.currentAsset }));
      } catch (e) {
        throw errorOf(e, 'Could not delete the asset');
      }
    },

    assignAsset: (id, employeeId, notes, meta = {}) =>
      assetAction(() => apiClient.post(`/assets/assets/${id}/assign`, { employeeId, notes, ...meta }), 'Could not assign the asset'),
    returnAsset: (id, data = {}) =>
      assetAction(() => apiClient.post(`/assets/assets/${id}/return`, data), 'Could not return the asset'),
    retireAsset: (id, data = {}) =>
      assetAction(() => apiClient.post(`/assets/assets/${id}/retire`, data), 'Could not retire the asset'),

    addAttachment: async (id, file) => {
      try {
        await apiClient.post(`/assets/assets/${id}/attachments`, {
          kind: file.kind, filename: file.filename, path: file.path,
          mime_type: file.mimeType, size_bytes: file.sizeBytes, metadata: file.metadata || {},
        });
        return await get().fetchAsset(id);
      } catch (e) {
        throw errorOf(e, 'Could not attach the file');
      }
    },

    updateAttachment: async (id, attachmentId, data) => {
      try {
        await apiClient.patch(`/assets/assets/${id}/attachments/${attachmentId}`, data);
        return await get().fetchAsset(id);
      } catch (e) {
        throw errorOf(e, 'Could not update the file');
      }
    },

    deleteAttachment: async (id, attachmentId) => {
      try {
        await apiClient.delete(`/assets/assets/${id}/attachments/${attachmentId}`);
        return await get().fetchAsset(id);
      } catch (e) {
        throw errorOf(e, 'Could not remove the file');
      }
    },

    fetchRequests: async () => {
      try {
        const rows = dataOf(await apiClient.get('/assets/requests'));
        set({ requests: Array.isArray(rows) ? rows.map(mapRequest) : [] });
      } catch (e: any) {
        set({ error: errorOf(e, 'Could not load requests').message });
      }
    },

    createRequest: async (data) => {
      try {
        const created = dataOf(await apiClient.post('/assets/requests', data));
        const req = mapRequest(created);
        set((s) => ({ requests: [req, ...s.requests.filter((r) => r.id !== req.id)] }));
        return req;
      } catch (e) {
        throw errorOf(e, 'Could not submit the request');
      }
    },

    updateRequestStatus: async (id, status, meta?: Record<string, any>) => {
      try {
        const payload = meta ? { status, ...meta } : { status };
        const updated = mapRequest(dataOf(await apiClient.patch(`/assets/requests/${id}`, payload)));
        set((s) => ({ requests: s.requests.map((r) => (r.id === id ? updated : r)) }));
      } catch (e) {
        throw errorOf(e, 'Could not update the request');
      }
    },

    fulfillRequest: async (id, assetId) => {
      try {
        const updated = mapRequest(dataOf(await apiClient.post(`/assets/requests/${id}/fulfill`, { assetId })));
        set((s) => ({ requests: s.requests.map((r) => (r.id === id ? updated : r)) }));
        await get().fetchAssets();
      } catch (e) {
        throw errorOf(e, 'Could not hand over the asset');
      }
    },

    fetchMaintenance: async (filters) => {
      try {
        const rows = dataOf(await apiClient.get('/assets/maintenance', { params: filters }));
        set({ maintenance: Array.isArray(rows) ? rows.map(mapJob) : [] });
      } catch (e: any) {
        set({ error: errorOf(e, 'Could not load service schedules').message });
      }
    },

    createMaintenance: async (data) => {
      try {
        const job = mapJob(dataOf(await apiClient.post('/assets/maintenance', data)));
        set((s) => ({ maintenance: [job, ...s.maintenance] }));
        return job;
      } catch (e) {
        throw errorOf(e, 'Could not schedule the service');
      }
    },

    updateMaintenance: async (id, data) => {
      try {
        const job = mapJob(dataOf(await apiClient.patch(`/assets/maintenance/${id}`, data)));
        set((s) => ({ maintenance: s.maintenance.map((m) => (m.id === id ? job : m)) }));
        // Starting/finishing work changes the asset's status.
        if (data.status) await get().fetchAssets();
      } catch (e) {
        throw errorOf(e, 'Could not update the service job');
      }
    },

    deleteMaintenance: async (id) => {
      try {
        await apiClient.delete(`/assets/maintenance/${id}`);
        set((s) => ({ maintenance: s.maintenance.filter((m) => m.id !== id) }));
      } catch (e) {
        throw errorOf(e, 'Could not delete the service job');
      }
    },

    fetchPurchaseOrders: async () => {
      try {
        const rows = dataOf(await apiClient.get('/assets/purchase-orders'));
        set({ purchaseOrders: Array.isArray(rows) ? rows.map(mapPo) : [] });
      } catch (e: any) {
        set({ error: errorOf(e, 'Could not load purchase orders').message });
      }
    },

    nextPoNumber: async () => {
      try {
        return dataOf(await apiClient.get('/assets/purchase-orders/next-number')).po_number;
      } catch (e) {
        throw errorOf(e, 'Could not generate a PO number');
      }
    },

    createPurchaseOrder: async (data) => {
      try {
        const po = mapPo(dataOf(await apiClient.post('/assets/purchase-orders', data)));
        set((s) => ({ purchaseOrders: [po, ...s.purchaseOrders] }));
        return po;
      } catch (e) {
        throw errorOf(e, 'Could not create the purchase order');
      }
    },

    updatePurchaseOrder: async (id, data) => {
      try {
        const po = mapPo(dataOf(await apiClient.patch(`/assets/purchase-orders/${id}`, data)));
        set((s) => ({ purchaseOrders: s.purchaseOrders.map((p) => (p.id === id ? po : p)) }));
      } catch (e) {
        throw errorOf(e, 'Could not update the purchase order');
      }
    },

    receivePurchaseOrder: async (id) => {
      try {
        const result = dataOf(await apiClient.post(`/assets/purchase-orders/${id}/receive`));
        const po = mapPo(result);
        set((s) => ({ purchaseOrders: s.purchaseOrders.map((p) => (p.id === id ? po : p)) }));
        await get().fetchAssets();
        return (result.created_asset_ids || []).length;
      } catch (e) {
        throw errorOf(e, 'Could not receive the purchase order');
      }
    },

    deletePurchaseOrder: async (id) => {
      try {
        await apiClient.delete(`/assets/purchase-orders/${id}`);
        set((s) => ({ purchaseOrders: s.purchaseOrders.filter((p) => p.id !== id) }));
      } catch (e) {
        throw errorOf(e, 'Could not delete the purchase order');
      }
    },

    fetchHistory: async (filters) => {
      try {
        const logs = (dataOf(await apiClient.get('/assets/history', { params: filters })) || []).map(mapLog);
        if (!filters?.assetId) set({ auditLogs: logs });
        return logs;
      } catch (e: any) {
        const err = errorOf(e, 'Could not load history');
        set({ error: err.message });
        throw err;
      }
    },

    clearCurrentAsset: () => set({ currentAsset: null }),
    clearError: () => set({ error: null }),
  };
});
